import { Color, SpriteMaterial, Vector3 } from "three";
import {
  BALL_RADIUS,
  cometPosAt,
  planetPosAt,
  simulateShot,
  targetRadiusAt,
  type BallState,
  type HoleLayout,
  type ShotInput,
  type ShotResult,
  type ShotSample,
} from "@shared/physics";
import * as courseModule from "@shared/course";
import {
  HOLES_PER_COURSE,
  buildDailyCourse,
  buildHole,
  courseDateAt,
  type DailyCourse,
} from "@shared/course";
import { seedFromString } from "@shared/rng";
import type { SceneCtx } from "../scene";
import type { Identity } from "../handle";
import {
  buildGhostTrails,
  buildHoleVisuals,
  setAimGuide,
  setPowerRing,
  setTrajectoryPreview,
  updateTrail,
  type GhostTrails,
  type HoleVisuals,
} from "./render";
import { installAim, type AimInput } from "./aim";
import { HoleCamera } from "./camera";
import { createSparks } from "./sparks";
import { installJoystick } from "./joystick";
import { sfx, startCharge } from "../audio";
import {
  getHudRefs,
  setHole,
  setPower,
  setPowerVisible,
  setStrokes,
  setTotal,
  showBanner,
  showToast,
} from "./hud";
import {
  apiCompleteRound,
  apiGetRound,
  apiGhostShots,
  apiPresence,
  apiSubmitShot,
  type CompleteResponse,
} from "../api";
import { getStoredToken } from "../token";
import { mountLeaderboard } from "./leaderboard";
import { showSummary } from "./summary";
import { installTicker } from "./ticker";
import { mountSettings } from "./settings";
import { mountPortals, type PortalHandle } from "./portal";
import { mountFirstHoleTutorial, type TutorialHandle } from "./tutorial";
import { biomeForHole } from "@shared/biome";
import { setActiveTheme } from "../theme";

type Phase = "idle" | "aiming" | "flying" | "done" | "summary";

// Dev helper — when running locally / previewing we sometimes want to
// play a specific day's course (to debug generator escalation, ghosts,
// OG cards, etc.) without waiting for the 13:37 UTC rollover. Accept
// either `?date=YYYY-MM-DD` or `?courseDate=YYYY-MM-DD`. Ignored on
// prod traffic because regular users don't set these params.
function resolveDailyDate(): string {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("date") ?? params.get("courseDate");
    if (raw && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  } catch {
    /* fall through to today */
  }
  return courseDateAt(Date.now());
}

export type GameMode =
  | {
      kind: "daily";
      /** Server rejected score submission because this handle is already
       *  on today's leaderboard. Tear down the round and bounce the user
       *  back to the welcome screen so they can pick another name. */
      onNameTaken?: () => void;
    }
  | {
      kind: "endless";
      /** Stable seed string for this endless session (seed per hole is
       *  derived from this + the current hole index). */
      rngSeed: string;
      /** Optional callback: user asked to return to the mode chooser. */
      onExitToMenu?: () => void;
    };

export interface GameHandle {
  dispose(): void;
}

export function startGame(
  scene: SceneCtx,
  identity: Identity,
  mode: GameMode = { kind: "daily" },
): GameHandle {
  const hud = getHudRefs();
  // Endless mode doesn't build a fixed 9-hole course — holes are
  // generated on demand via `buildHoleAtEscalation` and the round just
  // keeps going. We still synthesize a `DailyCourse` shape so the
  // existing HUD/leaderboard code paths don't branch on mode for
  // everything. Endless starts with a single generated hole and we
  // extend the `holes` array lazily as the player advances.
  const course: DailyCourse =
    mode.kind === "daily"
      ? buildDailyCourse(resolveDailyDate())
      : buildEndlessPrefix(mode.rngSeed);
  const coursePillPrefix = document
    .getElementById("hud-course")
    ?.querySelector<HTMLElement>(".dim");
  if (mode.kind === "daily") {
    hud.courseDate.textContent = course.date;
    if (coursePillPrefix) coursePillPrefix.textContent = "Daily";
  } else {
    hud.courseDate.textContent = "";
    if (coursePillPrefix) coursePillPrefix.textContent = "♾️ Practice";
  }

  const token = getStoredToken() ?? "";
  const isEndless = mode.kind === "endless";
  const isLocalOnly = isEndless;

  // Leaderboard panel (opens on demand).
  const leaderboardBtn = document.getElementById(
    "hud-leaderboard-btn",
  ) as HTMLElement | null;
  const leaderboardPanel = document.getElementById("leaderboard")!;
  const leaderboardClose = document.getElementById("leaderboard-close")!;
  const summaryRoot = document.getElementById("summary")!;
  // In Practice (endless) mode, daily-specific UI is hidden: no live
  // leaderboard, no ticker polling against someone else's seed.
  const leaderboard = !isLocalOnly
    ? mountLeaderboard(
        leaderboardPanel,
        leaderboardBtn as HTMLElement,
        leaderboardClose,
        { courseDate: course.date, myHandle: identity.handle },
      )
    : null;
  if (isLocalOnly && leaderboardBtn) leaderboardBtn.style.display = "none";

  // Live event ticker (other players' holings, polled from the server).
  const tickerRoot = document.getElementById("ticker")!;
  const ticker = !isLocalOnly
    ? installTicker({
        container: tickerRoot,
        courseDate: course.date,
        holes: course.holes,
        myHandle: identity.handle,
      })
    : null;

  // Online-players pill — polls the server every 20s and renders a
  // small HUD badge showing how many rounds have had activity in the
  // last 2 minutes. Hidden entirely in practice/endless to keep the
  // local-only chrome minimal.
  const presencePill = document.getElementById("hud-presence");
  const presenceCountEl = document.getElementById("hud-presence-count");
  const presenceLabelEl = document.getElementById("hud-presence-label");
  const holePresencePill = document.getElementById("hud-hole-presence");
  const holePresenceCountEl = document.getElementById(
    "hud-hole-presence-count",
  );
  // Cache the most recent perHole histogram so loadHole() can update
  // the pill immediately on hole transitions without waiting for the
  // next 20s poll cycle.
  let lastPerHole: number[] = [];
  const refreshHolePresencePill = () => {
    if (!holePresencePill || !holePresenceCountEl) return;
    if (isLocalOnly) {
      holePresencePill.hidden = true;
      return;
    }
    const here = lastPerHole[holeIndex] ?? 0;
    // Threshold of 2 — single players seeing "1 here" reads as
    // "just me" rather than social proof. 2+ means at least one peer.
    if (here >= 2) {
      holePresenceCountEl.textContent = String(here);
      holePresencePill.hidden = false;
    } else {
      holePresencePill.hidden = true;
    }
  };
  let presenceTimer: number | null = null;
  // Match the welcome card's ≥10 social-proof threshold so a "0
  // playing" or "1 playing" pill doesn't read as broken / lonely.
  const PRESENCE_THRESHOLD = 10;
  if (!isLocalOnly && presencePill && presenceCountEl && presenceLabelEl) {
    // Default hidden — refreshPresence reveals it only when the
    // count crosses the threshold.
    presencePill.hidden = true;
    const refreshPresence = async () => {
      try {
        const res = await apiPresence();
        if (res.ok) {
          presenceCountEl.textContent = String(res.active);
          presenceLabelEl.textContent = "playing";
          presencePill.hidden = res.active < PRESENCE_THRESHOLD;
          if (res.perHole) {
            lastPerHole = res.perHole;
            refreshHolePresencePill();
          }
        }
      } catch {
        // Network hiccup — leave the current visibility on screen.
      }
    };
    void refreshPresence();
    presenceTimer = window.setInterval(refreshPresence, 20_000);
  }

  // Settings panel (volume slider + mute toggle).
  const settingsPanel = document.getElementById("settings")!;
  const settingsBtn = document.getElementById("hud-settings-btn")!;
  const settingsClose = document.getElementById("settings-close")!;
  const settings = mountSettings(settingsPanel, settingsBtn, settingsClose);

  let hoveredGhostHandle: string | null = null;

  if (leaderboard) {
    leaderboard.onHover = (handle) => {
      hoveredGhostHandle = handle;
    };
  }

  // Per-round state.
  let portals: PortalHandle | null = null;
  // First-hole tutorial overlay. Self-dismisses on first canvas
  // pointerdown / 12s timeout / localStorage flag; we only hold a
  // handle so the scene teardown can defensively kill it if the user
  // navigates away mid-tutorial.
  let tutorial: TutorialHandle | null = null;
  let holeIndex = -1;
  let hole: HoleLayout = course.holes[0]!;
  let visuals: HoleVisuals | null = null;
  let ghostTrails: GhostTrails | null = null;
  let ballState: BallState = { x: hole.tee.x, z: hole.tee.z, vx: 0, vz: 0 };
  let phase: Phase = "idle";
  let strokes = 0;
  const strokesPerHole: number[] = new Array(HOLES_PER_COURSE).fill(0);
  // Trajectory of the holed (winning) shot per hole — used by the
  // share card to render a mini-preview of the player's best line.
  // Populated by `onShotSettled` on `outcome === "holed"`.
  const holedShotSamples: (ShotSample[] | null)[] = new Array(
    HOLES_PER_COURSE,
  ).fill(null);
  // Time the current hole started (scene.clock seconds) so gates can
  // animate on a hole-local clock — phase 0 at load, independent of
  // how long the session has been running.
  let holeStartT = 0;

  // Endless chrome — escalation pip next to the hole label. Mounted
  // after `holeIndex` is in scope so the closure it captures isn't
  // hitting the TDZ on its initial render.
  const endlessChrome = mountEndlessChrome(mode, () => holeIndex);

  const camera = new HoleCamera(scene.camera, hole);

  hud.camBtn.addEventListener("click", () => {
    const isBird = !camera.isBirdCam();
    camera.setBirdCam(isBird);
    hud.camBtn.style.color = isBird ? "var(--accent)" : "var(--ink)";
    sfx.uiClick();
  });

  // Collision sparks — one persistent particle pool shared across holes.
  const sparks = createSparks();
  scene.scene.add(sparks.points);
  // Per-planet contact cooldown so a single grazing pass emits one burst.
  let planetContactCooldown: number[] = [];

  // Audio handle for the charge drone (held while aiming).
  let chargeAudio: ReturnType<typeof startCharge> | null = null;

  // Virtual joystick overlay — visual feedback for the aim drag.
  const joystick = installJoystick(scene.renderer.domElement);

  // Shot playback state.
  let playback: {
    samples: ShotSample[];
    result: ShotResult;
    startAt: number;
    onEnd: () => void;
  } | null = null;

  // Serialized chain for shot submissions. Without this, fire-and-forget
  // /api/shot/submit calls can race when the player advances holes faster
  // than the network — the server processes them out of order and rejects
  // the later one with `prev_incomplete`. We let the local sim + visual
  // playback run independently (so input never feels gated on the network)
  // and only serialize the network round-trips themselves.
  let submitChain: Promise<unknown> = Promise.resolve();

  const ballPos = () =>
    new Vector3(ballState.x, BALL_RADIUS + 0.06, ballState.z);

  // ---------- hole loading ----------
  async function loadHole(
    i: number,
    resume?: { strokes: number; ballPosition: { x: number; z: number } | null },
  ): Promise<void> {
    // Tear down previous.
    if (visuals) {
      scene.scene.remove(visuals.root);
      visuals.dispose();
      visuals = null;
    }
    if (ghostTrails) {
      scene.scene.remove(ghostTrails.root);
      ghostTrails.dispose();
      ghostTrails = null;
    }
    if (portals) {
      portals.dispose();
      portals = null;
    }

    holeIndex = i;
    // Endless mode: generate the hole on demand via v7's
    // buildHoleAtEscalation (cast through `any` while the v7 shared
    // API lives in the concurrent branch). We cache the resulting
    // holes on `course.holes` so the existing rendering pipeline
    // (ghost-less, local-only) doesn't need a new branch.
    if (isEndless && !course.holes[i]) {
      const seed = seedFromString(
        `endless/${(mode as { rngSeed: string }).rngSeed}/${i}`,
      );
      // Endless H1/H2 mirror the daily teaching curve: H1 is the easy
      // intro (one planet, non-blocking), H2 introduces the corridor
      // blocker. From H3 on, continuous escalation keeps climbing past
      // 1.0 so endless stays endlessly harder.
      let built: HoleLayout;
      if (i === 0) {
        built = (courseModule as any).buildIntroHole(i, seed);
      } else if (i === 1) {
        built = (courseModule as any).buildBlockerHole(i, seed);
      } else {
        const escalation = escalationFor(i);
        built = (courseModule as any).buildHoleAtEscalation
          ? (courseModule as any).buildHoleAtEscalation(i, seed, escalation)
          : buildHole(i, seed, { escalation: Math.min(1, escalation) });
      }
      course.holes[i] = built;
    }
    hole = course.holes[i]!;
    const biomeKey =
      mode.kind === "endless" ? mode.rngSeed : course.date;
    setActiveTheme(biomeForHole(biomeKey, i));
    scene.backdrop.applyTheme();
    visuals = buildHoleVisuals(hole);
    scene.scene.add(visuals.root);
    camera.setHole(hole);
    planetContactCooldown = new Array(hole.planets.length).fill(0);

    // Vibeverse portals — Practice (endless) mode only, and only on
    // H6-H7 (0-indexed 5-6) so the screen stays clean on the intro
    // curve and the late brutal stretch. Daily mode never shows them
    // so the leaderboard run is uncluttered.
    if (isEndless && i >= 5 && i <= 6) {
      portals = mountPortals(scene.scene, hole);
    }

    const start = resume?.ballPosition ?? { x: hole.tee.x, z: hole.tee.z };
    ballState = { x: start.x, z: start.z, vx: 0, vz: 0 };
    strokes = resume?.strokes ?? 0;
    phase = "idle";
    // Reset hole-local clock so gate animations restart at phase 0.
    holeStartT = scene.clock.getElapsedTime();
    setHole(hud, hole.index, hole.par, isEndless ? "endless" : "standard");
    setStrokes(hud, strokes);
    setTotal(hud, totalStrokes());
    endlessChrome?.refresh();
    // New hole — flip the "X here" pill to whatever count the cached
    // histogram has for this hole. Stays hidden if perHole isn't loaded
    // yet (first poll still in flight); the next poll cycle will update.
    refreshHolePresencePill();
    if (visuals) {
      visuals.ball.position.set(ballState.x, BALL_RADIUS + 0.06, ballState.z);
    }

    // Fetch and render ghost trails asynchronously — don't block play.
    void loadGhostsForHole(i);
  }

  async function loadGhostsForHole(i: number): Promise<void> {
    // Practice runs on a player-local seed that no one else has played —
    // there are no ghost trails to fetch, skip the request.
    if (isLocalOnly) return;
    try {
      const res = await apiGhostShots(i, course.date, 20);
      // Only render if we're still on this hole.
      if (holeIndex !== i || !visuals) return;
      const trails = buildGhostTrails(
        res.shots.map((s) => ({
          handle: s.handle,
          samples: s.samples.map(sam => ({ x: sam.x, y: 0.06, z: sam.z })),
          strokes: s.strokes,
          outcome: s.outcome,
        })),
      );
      ghostTrails = trails;
      scene.scene.add(trails.root);
    } catch {
      /* leaderboard/shots endpoints may 404 in dev; ignore */
    }
  }

  function totalStrokes(): number {
    return strokesPerHole.reduce((a, b) => a + b, 0);
  }

  // ---------- aim / shoot ----------
  const aimDispose = installAim(scene.renderer.domElement, scene.camera, {
    getBallPosition: ballPos,
    isInteractive: () => phase === "idle",
    onAimStart() {
      phase = "aiming";
      chargeAudio = startCharge();
      setPowerVisible(true);
    },
    onAimMove(input: AimInput) {
      if (!visuals) return;
      setPower(hud, input.power);
      const from = ballPos();
      const guide =
        input.power > 0
          ? new Vector3(
              ballState.x + input.aimX * (3 + input.power * 9),
              0.22,
              ballState.z + input.aimZ * (3 + input.power * 9),
            )
          : null;
      setAimGuide(visuals, from, guide);
      setPowerRing(visuals, from, input.power);
      setTrajectoryPreview(
        visuals,
        hole,
        { x: ballState.x, z: ballState.z },
        input,
      );
      chargeAudio?.update(input.power);
    },
    onAimCancel() {
      if (!visuals) return;
      phase = "idle";
      setPower(hud, 0);
      setPowerVisible(false);
      setAimGuide(visuals, ballPos(), null);
      setPowerRing(visuals, ballPos(), 0);
      setTrajectoryPreview(visuals, hole, { x: ballState.x, z: ballState.z }, null);
      chargeAudio?.release();
      chargeAudio = null;
    },
    onShoot(input: AimInput) {
      if (!visuals) return;
      setPower(hud, 0);
      setPowerVisible(false);
      setAimGuide(visuals, ballPos(), null);
      setPowerRing(visuals, ballPos(), 0);
      setTrajectoryPreview(visuals, hole, { x: ballState.x, z: ballState.z }, null);
      chargeAudio?.release();
      chargeAudio = null;
      sfx.shot();
      // Shot cam: camera dollies behind the ball, looking ahead.
      camera.beginShotCam(
        { x: ballState.x, z: ballState.z },
        { x: input.aimX, z: input.aimZ },
      );
      void fireShot(input);
    },
  });

  async function fireShot(input: ShotInput): Promise<void> {
    if (!visuals) return;
    const startState = { ...ballState };
    strokes++;
    strokesPerHole[holeIndex] = strokes;
    setStrokes(hud, strokes);
    setTotal(hud, totalStrokes());

    // Local simulation for immediate playback; server re-sim is authoritative.
    const localResult = simulateShot(hole, startState, input);
    phase = "flying";
    const onPlaybackEnd = () => onShotSettled(localResult);
    playback = {
      samples: localResult.samples,
      result: localResult,
      startAt: scene.clock.getElapsedTime(),
      onEnd: onPlaybackEnd,
    };
    updateTrail(visuals, localResult.samples.map(s => ({ x: s.x, y: 0.18, z: s.z })), 0);

    // Submit to server for anti-cheat + persistence. Client and server share
    // shared/physics.ts so stroke counts won't diverge; we only surface
    // errors (e.g. auth, hole already complete from a prior session).
    // Practice mode is client-only — no submission, no leaderboard.
    if (isLocalOnly) return;
    const submittedHole = holeIndex;
    // Enqueue on the serial chain so submissions arrive at the server in
    // the same order they fired — fixes the `prev_incomplete` race when
    // a player rolls into the next hole before the previous shot's
    // round-trip completes.
    const prev = submitChain;
    submitChain = (async () => {
      try {
        await prev;
      } catch {
        /* previous submission already swallowed its own errors */
      }
      try {
        const res = await apiSubmitShot(
          identity.handle,
          token,
          course.date,
          submittedHole,
          input,
        );
        if (!res.ok && holeIndex === submittedHole) {
          showToast(hud, `Shot rejected: ${res.reason}`, 2400);
        }
      } catch {
        // Network hiccup. The client can keep playing locally; final
        // /api/score/complete will surface any mismatch.
      }
    })();
  }

  function onShotSettled(result: ShotResult): void {
    if (!visuals) return;
    ballState = result.endState;
    playback = null;

    if (result.outcome === "holed") {
      phase = "done";
      // Capture this winning trajectory so the share card can render
      // a mini-preview of the player's line on their best hole.
      holedShotSamples[holeIndex] = result.samples;
      const delta = strokes - hole.par;
      // Big celebratory banner for the major outcomes (hole-in-one,
      // eagle, birdie, par). Bogey+ stays a quiet toast so the UI
      // doesn't lionize mediocrity.
      if (strokes === 1) {
        showBanner(hud, {
          title: "HOLE IN ONE!",
          subtitle: "INCREDIBLE SHOT",
          crown: "👑",
          kind: "celebrate",
          ms: 2400,
        });
      } else if (delta === -2) {
        showBanner(hud, {
          title: "EAGLE!",
          subtitle: `${strokes} strokes · 2 under`,
          crown: "🦅",
          kind: "celebrate",
          ms: 2200,
        });
      } else if (delta === -1) {
        showBanner(hud, {
          title: "BIRDIE!",
          subtitle: `${strokes} strokes · 1 under`,
          crown: "🐦",
          kind: "celebrate",
          ms: 2000,
        });
      } else if (delta === 0) {
        showBanner(hud, {
          title: "PAR",
          subtitle: `${strokes} strokes`,
          crown: "✨",
          kind: "par",
          ms: 1700,
        });
      } else {
        showToast(hud, `+${delta} · ${strokes} strokes`, 1600);
      }
      if (strokes === 1) sfx.holeInOne();
      else sfx.hole(delta);
      // Advance after a beat so the player sees the ball drop.
      setTimeout(() => {
        if (isEndless) {
          // Endless never "finishes" — it just keeps spawning harder
          // holes. The player exits via the 🏠 back-to-menu button.
          void loadHole(holeIndex + 1);
        } else if (holeIndex < HOLES_PER_COURSE - 1) {
          void loadHole(holeIndex + 1);
        } else {
          void finishRound();
        }
      }, 1500);
    } else {
      phase = "idle";
      if (result.outcome === "out_of_bounds") {
        showBanner(hud, {
          title: "LOST IN SPACE",
          subtitle: "Ball reset to tee",
          crown: "🛰️",
          kind: "warn",
          ms: 1800,
        });
        sfx.oob();
        ballState = { x: hole.tee.x, z: hole.tee.z, vx: 0, vz: 0 };
        if (visuals) {
          visuals.ball.position.set(
            ballState.x,
            BALL_RADIUS + 0.06,
            ballState.z,
          );
        }
      } else if (result.outcome === "comet_hit") {
        showBanner(hud, {
          title: "COMET STRIKE!",
          subtitle: "Ball vaporized · reset to tee",
          crown: "☄️",
          kind: "warn",
          ms: 2000,
        });
        sfx.oob();
        ballState = { x: hole.tee.x, z: hole.tee.z, vx: 0, vz: 0 };
        if (visuals) {
          visuals.ball.position.set(
            ballState.x,
            BALL_RADIUS + 0.06,
            ballState.z,
          );
        }
      }
    }
  }

  async function finishRound(options?: {
    /** Skip the server call; use pre-computed completion (resume path). */
    preComputed?: CompleteResponse;
  }): Promise<void> {
    phase = "summary";
    sfx.winFanfare();
    let complete: CompleteResponse | null = options?.preComputed ?? null;
    if (!isLocalOnly && !complete) {
      try {
        complete = await apiCompleteRound(identity.handle, token, course.date);
        if (complete.ok) {
          // Refresh leaderboard so our score is reflected when panel opens.
          void leaderboard?.refresh();
        } else if (
          complete.reason === "name_taken_today" &&
          mode.kind === "daily" &&
          mode.onNameTaken
        ) {
          showToast(
            hud,
            `"${identity.handle}" is already on today's leaderboard. Pick another name.`,
            4200,
          );
          // Small delay so the toast is visible before the screen flips
          // back to the welcome card.
          setTimeout(() => mode.onNameTaken?.(), 1800);
          return;
        }
      } catch {
        complete = null;
      }
    }

    // When resuming after a server restart lost the per-hole counts, trust
    // the complete.totalStrokes (authoritative from InstantDB) over the
    // zero-filled perHole sum — otherwise the delta label in the summary
    // reads as "X under par" against zero.
    const totalForSummary =
      complete?.ok && complete.totalStrokes > 0
        ? complete.totalStrokes
        : totalStrokes();
    // Pick the "feature" hole for the trail snapshot: lowest delta vs.
    // par (best performance); on a tie, prefer the lower hole index so
    // the card feels like "the player nailed the first birdie" rather
    // than "the card rerolls each time". Only holes the player actually
    // completed with a captured trajectory are eligible.
    let featureHoleIdx: number | null = null;
    let featureDelta = Infinity;
    for (let i = 0; i < HOLES_PER_COURSE; i++) {
      if (!holedShotSamples[i]) continue;
      const par = course.holes[i]?.par ?? 0;
      const d = strokesPerHole[i]! - par;
      if (d < featureDelta) {
        featureDelta = d;
        featureHoleIdx = i;
      }
    }
    const feature =
      featureHoleIdx !== null
        ? {
            holeIndex: featureHoleIdx,
            layout: course.holes[featureHoleIdx]!,
            samples: holedShotSamples[featureHoleIdx]!,
          }
        : null;
    showSummary(
      summaryRoot,
      {
        courseDate: course.date,
        perHole: strokesPerHole.slice(),
        pars: course.holes.map((h) => h.par),
        totalStrokes: totalForSummary,
        complete,
        handle: identity.handle,
        flag: identity.flag,
        countryCode: identity.countryCode,
        practice: null,
        featureHole: feature,
      },
      () => leaderboard?.open(),
    );
  }

  // Track last-frame ball pos so we can detect close passes to planets (→ shake).
  let lastShakeSampleIdx = -1;

  // Set once the ball enters a portal so we don't fire the warp + redirect
  // multiple times across frames while the overlay fades in.
  let warping = false;

  function tryPortalEntry(x: number, z: number): void {
    if (warping || !portals) return;
    const dest = portals.checkBall(x, z);
    if (!dest) return;
    warping = true;
    sfx.hole(-1);
    triggerWarpOverlay(x, z);
    // Let the overlay flash for ~650ms before committing the navigation —
    // feels like "falling into" the portal rather than a teleport cut.
    setTimeout(() => {
      window.location.href = dest;
    }, 650);
  }

  // ---------- frame loop ----------
  const unsubFrame = scene.onFrame((t) => {
    if (!visuals) return;

    // Animate target halo + accretion disk + particle orbit +
    // photon ring + billboard lens ring.
    setUniformTime(visuals.targetHalo, t);
    visuals.targetDiskMat.uniforms.uTime!.value = t;
    for (const m of visuals.targetExtraMats) {
      if (m.uniforms.uTime) m.uniforms.uTime.value = t;
    }
    const pMat = visuals.targetParticles.material as unknown as {
      uniforms?: { uTime?: { value: number } };
    };
    if (pMat?.uniforms?.uTime) pMat.uniforms.uTime.value = t;
    // Billboard the lensing ring at the camera so the torus axis
    // aligns with view direction and the ring always draws face-on.
    visuals.targetLensRing.lookAt(scene.camera.position);

    // Sim time into the current shot — 0 while aiming, elapsed since
    // flight start while flying. Moving planets + pulsing targets are
    // anchored to this so the physics and the render stay in lockstep.
    const simT =
      phase === "flying" && playback
        ? t - playback.startAt
        : 0;

    // Spin planets + gas-giant shader time + keep rings oriented.
    for (let i = 0; i < visuals.planets.length; i++) {
      const p = visuals.planets[i]!;
      p.rotation.y += (p.userData.spin ?? 0.02) * 0.02;
      setUniformTime(p, t);
    }
    // Orbiting planets (v9) — move the planet root (and its gravity
    // well) to the sim-time position. For static planets this is a
    // no-op; render-order is preserved because we only write .position.
    for (let i = 0; i < hole.planets.length; i++) {
      const lp = hole.planets[i]!;
      if (!lp.orbit) continue;
      const pos = planetPosAt(lp, simT);
      const root = visuals.planetRoots[i];
      if (root) {
        root.position.x = pos.x;
        root.position.z = pos.z;
      }
      const well = visuals.gravityWells[i];
      if (well) {
        well.position.x = pos.x;
        well.position.z = pos.z;
      }
    }
    // Pulse the ground gravity-well disks in lockstep with planet spin.
    for (let i = 0; i < visuals.gravityWells.length; i++) {
      setUniformTime(visuals.gravityWells[i]!, t);
    }
    // Pulsing target (v9) — scale the target halo, disk, and particles
    // by the effective-radius-over-base-radius ratio so the capture
    // footprint always matches what physics is using this step.
    if (hole.target.pulse) {
      const effR = targetRadiusAt(hole.target, simT);
      const scale = Math.max(0.1, effR / hole.target.radius);
      visuals.targetHalo.scale.setScalar(scale);
      visuals.targetDisk.scale.setScalar(scale);
      visuals.targetParticles.scale.setScalar(scale);
    }
    // Wormhole accretion ring animation.
    for (let i = 0; i < visuals.wormholes.length; i++) {
      const w = visuals.wormholes[i]!;
      const ringMat = (w.userData as { ringMat?: { uniforms?: { uTime?: { value: number } } } })
        .ringMat;
      if (ringMat?.uniforms?.uTime) ringMat.uniforms.uTime.value = t;
    }
    // Drift the orbiting perimeter dust.
    visuals.boundaryParticles.update(t);

    // Orbital-chart disc / shimmer / ring animation — same shared
    // uniform block, so one uTime write covers rotating rings + sweep.
    if (visuals.boundaryMat.uniforms.uTime) {
      visuals.boundaryMat.uniforms.uTime.value = t;
    }

    // Wind streamers — drift particles along hole.windAccel.
    if (visuals.windStreamers) {
      visuals.windStreamers.update(t);
    }

    // Comet hazards — sweep each head along its track using simT
    // while flying (matches physics) and hole-local wall-clock time
    // while idle so the threat stays animated between shots. Also
    // ticks uTime on auxiliary shader mats + spins the spike sprite.
    if (visuals.comets.length > 0) {
      const cometT = phase === "flying" && playback ? simT : Math.max(0, t - holeStartT);
      for (const c of visuals.comets) {
        const pos = cometPosAt(c.comet, cometT);
        c.head.position.x = pos.x;
        c.head.position.z = pos.z;
        if (c.headMat.uniforms.uTime) c.headMat.uniforms.uTime.value = t;
        const extras = (c.head.userData as { extras?: { uniforms?: { uTime?: { value: number } } }[] }).extras;
        if (extras) {
          for (const m of extras) {
            if (m.uniforms?.uTime) m.uniforms.uTime.value = t;
          }
        }
        // Spin the star-burst spike sprite for added menace.
        c.head.traverse((obj) => {
          if (obj.type === "Sprite" && typeof (obj.userData as { spinRate?: number }).spinRate === "number") {
            const mat = (obj as unknown as { material: SpriteMaterial }).material;
            // SpriteMaterial has a `rotation` property (radians).
            mat.rotation = t * ((obj.userData as { spinRate: number }).spinRate);
          }
        });
      }
    }

    // Gate animation — fade uClosed between 0 and 1 with a ~150ms
    // smoothstep window so the bar doesn't pop. Leading-edge glow
    // intensifies in the last 300ms before closing so the player can
    // time their shot. `tLocal` is sim-seconds-since-hole-load so
    // gate phase restarts when a new hole loads.
    const tLocal = Math.max(0, t - holeStartT);
    for (const g of visuals.gates) {
      const gt = g.gate;
      const period = Math.max(0.0001, gt.closedSeconds + gt.openSeconds);
      const phase = ((tLocal / period + gt.phase) % 1 + 1) % 1;
      const closedFrac = gt.closedSeconds / period;
      // True while in the "closed" portion of the cycle.
      const rawClosed = phase < closedFrac ? 1 : 0;
      // Crossfade with a 150ms window on either side of the transition.
      const windowSec = 0.15;
      const windowPhase = windowSec / period;
      let target = rawClosed;
      if (phase < windowPhase) {
        target = smoothstep01(phase / windowPhase) * (rawClosed === 1 ? 1 : 0)
          + (1 - smoothstep01(phase / windowPhase)) * (rawClosed === 1 ? 0 : 1);
      } else if (phase > closedFrac - windowPhase && phase < closedFrac) {
        const u = (phase - (closedFrac - windowPhase)) / windowPhase;
        target = 1 - smoothstep01(u);
      } else if (phase > 1 - windowPhase) {
        const u = (phase - (1 - windowPhase)) / windowPhase;
        target = smoothstep01(u);
      }
      // Smooth the crossfade further frame-to-frame so temporal
      // aliasing doesn't show.
      g.closedCrossfade = g.closedCrossfade * 0.82 + target * 0.18;
      g.barMat.uniforms.uClosed!.value = g.closedCrossfade;
      g.barMat.uniforms.uTime!.value = t;
      // Leading-edge glow: ramps up in the last 300ms of the open
      // window, then drops immediately on close so the urgency read
      // is crisp.
      const timeToClose =
        phase < closedFrac
          ? 0
          : (1 - phase) * period; // seconds until it closes again
      const leading = rawClosed === 0
        ? Math.max(0, 1 - timeToClose / 0.3)
        : 0;
      g.barMat.uniforms.uLeadingEdge!.value = leading;
    }

    // Vibeverse portals — always animate the torus/particles. The actual
    // entry check happens below in both the rest branch (after a settled
    // shot) and the flight branch (during ball playback), because a
    // fast-flying ball passes through the trigger in 1-2 frames and we
    // don't want to miss it.
    if (portals) {
      portals.update(t, scene.camera);
    }

    if (ghostTrails) {
      // Animate the gold Pro's Line pulse each frame (hidden in 3rd-person
      // view anyway; the conditional below controls visibility).
      ghostTrails.update?.(t);
      // Show ghost trails when zooming out, in intro, or when hovered in leaderboard
      const transition = camera.getBirdCamTransition();
      const isFar = transition > 0.05 || camera.isIntro();

      scene.setGodRaysStrength(1 - transition); // fade out post-processing bloom in bird cam

      ghostTrails.root.visible = isFar || hoveredGhostHandle !== null;

      if (ghostTrails.root.visible) {
        const factor = camera.isIntro() ? 1.0 : transition;

        // Stack / hide labels that pile up in screen-space (hole-in-one clusters
        // on H1-3 especially) before we apply the fade pass.
        ghostTrails.declutter?.(scene.camera);

        // Fade in based on transition
        ghostTrails.root.traverse((child) => {
          if (child.type === "Line" || child.type === "Sprite") {
            const mat = (child as any).material;
            if (mat && mat.opacity !== undefined) {
              const targetOp = child.userData.baseOpacity || mat.opacity;
              child.userData.baseOpacity = targetOp;

              let currentFactor = factor;

              // If hovering on a specific ghost, force it to be visible
              if (hoveredGhostHandle) {
                if (child.userData.handle === hoveredGhostHandle) {
                  currentFactor = 1.0;
                } else {
                  currentFactor = 0.05; // dim others heavily
                }
              }

              // Sprite labels may be dimmed or hidden by the declutter pass,
              // but always surface the label a leaderboard hover is asking for.
              const isHoverTarget =
                hoveredGhostHandle !== null &&
                child.userData.handle === hoveredGhostHandle;
              const clusterFade =
                child.type === "Sprite" &&
                !isHoverTarget &&
                typeof child.userData.clusterFade === "number"
                  ? child.userData.clusterFade
                  : 1;
              mat.opacity = targetOp * currentFactor * clusterFade;
              const wantVisible = currentFactor > 0.01 && clusterFade > 0.01;
              child.visible = wantVisible;
            }
          }
        });
      }
    }

    if (phase === "flying" && playback && visuals) {
      const elapsed = t - playback.startAt;
      const sampleInterval = 1 / 60;
      let idx = Math.floor(elapsed / sampleInterval);
      if (idx >= playback.samples.length) idx = playback.samples.length - 1;
      updateTrail(visuals, playback.samples.map(s => ({ x: s.x, y: 0.18, z: s.z })), idx + 1);
      const s = playback.samples[idx]!;
      const sNext =
        playback.samples[Math.min(idx + 1, playback.samples.length - 1)]!;
      const segT = Math.min(
        1,
        (elapsed - idx * sampleInterval) / sampleInterval,
      );
      // Wormhole teleport detection: consecutive samples can jump very
      // far apart (ball enters one portal, exits the other mid-tick).
      // Snap the render to the destination rather than zip-interpolate.
      const dxJump = sNext.x - s.x;
      const dzJump = sNext.z - s.z;
      const jumpLenSq = dxJump * dxJump + dzJump * dzJump;
      const teleported = jumpLenSq > 64; // > 8 units in one frame
      const x = teleported ? sNext.x : s.x + dxJump * segT;
      const z = teleported ? sNext.z : s.z + dzJump * segT;
      if (teleported && idx !== lastShakeSampleIdx) {
        // Play the warp sfx on the frame we actually cross portals.
        sfx.hole(-1);
        camera.shake(0.7);
        sparks.burst(x, 0, z, new Color("#a2ffe0"), 32);
      }
      visuals.ball.position.set(x, BALL_RADIUS + 0.06, z);
      visuals.boundaryMat.uniforms.uBallPos!.value.set(x, 0, z);
      camera.updateShotCamBall(x, z);
      camera.followBall(x, 0, z, 0.05, t);

      // Close-pass / collision shake + sparks.
      if (idx !== lastShakeSampleIdx) {
        lastShakeSampleIdx = idx;
        for (let i = 0; i < hole.planets.length; i++) {
          const pl = hole.planets[i]!;
          const dx = x - pl.cx;
          const dz = z - pl.cz;
          const d = Math.hypot(dx, dz);
          const near = pl.radius + BALL_RADIUS;
          if (d < near + 0.25) {
            camera.shake(0.45);
            // Burst sparks + thud on fresh contact only (cooldown 0.15s).
            if (planetContactCooldown[i]! < t) {
              planetContactCooldown[i] = t + 0.15;
              // Spark color biased toward the planet's surface tone.
              sparks.burst(x, 0, z, new Color("#ffb766"), 22);
              sfx.collide(1);
            }
          } else if (d > near + 0.8 && planetContactCooldown[i]! > t - 1) {
            // Clear cooldown once we've moved well away.
            planetContactCooldown[i] = 0;
          }
        }
      }

      // Portal entry during flight — fast balls would otherwise blow
      // through the trigger radius in a single frame.
      tryPortalEntry(x, z);

      if (idx >= playback.samples.length - 1) {
        const cb = playback.onEnd;
        playback = null;
        camera.endShotCam();
        lastShakeSampleIdx = -1;
        cb();
      }
    } else {
      camera.followBall(ballState.x, 0, ballState.z, 0.04, t);
      if (visuals) visuals.boundaryMat.uniforms.uBallPos!.value.set(ballState.x, 0, ballState.z);
      // Portal entry check at rest — covers the case where the player
      // aims a soft shot that comes to rest inside the trigger radius.
      tryPortalEntry(ballState.x, ballState.z);
    }

    setUniformTime(visuals.ball, t);
    sparks.update(t);
  });

  function setUniformTime(obj: { material?: unknown }, t: number) {
    const m = obj.material as
      | { uniforms?: { uTime?: { value: number } } }
      | undefined;
    if (m?.uniforms?.uTime) m.uniforms.uTime.value = t;
  }

  // GLSL smoothstep(0,1,x) ported to JS for gate crossfades.
  function smoothstep01(x: number): number {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    return x * x * (3 - 2 * x);
  }

  // Kick off round — resume server-side state if present, else start fresh.
  // Practice mode never touches the server; each session is fresh.
  void (async () => {
    let welcomed = false;
    if (!isLocalOnly) {
      try {
        const r = await apiGetRound(identity.handle, token, course.date);
        if (r.ok && r.state === "resume") {
          // Rehydrate per-hole strokes from server.
          for (let i = 0; i < HOLES_PER_COURSE; i++) {
            strokesPerHole[i] = r.strokesPerHole[i] ?? 0;
          }
          await loadHole(r.currentHole, {
            strokes: r.strokesPerHole[r.currentHole] ?? 0,
            ballPosition: r.ballPosition,
          });
          showBanner(hud, {
            title: "RESUMING RUN",
            subtitle: `Hole ${r.currentHole + 1} of ${HOLES_PER_COURSE}`,
            crown: "🌀",
            kind: "par",
            ms: 2200,
          });
          welcomed = true;
        } else if (r.ok && r.state === "complete") {
          for (let i = 0; i < HOLES_PER_COURSE; i++) {
            strokesPerHole[i] = r.strokesPerHole[i] ?? 0;
          }
          // Still need a hole in the scene behind the summary — pick hole 0.
          await loadHole(0);
          const c = r.completed;
          const preComputed: CompleteResponse | undefined = c
            ? {
                ok: true,
                totalStrokes: c.totalStrokes,
                rank: c.rank,
                totalPlayers: c.totalPlayers,
                perHole: r.strokesPerHole,
              }
            : undefined;
          await finishRound({ preComputed });
          welcomed = true;
        }
      } catch {
        // Any network/server hiccup → fall through to fresh start.
      }
    }
    if (!welcomed) {
      await loadHole(0);
      if (isEndless) {
        showBanner(hud, {
          title: "PRACTICE RUN",
          subtitle: "Difficulty keeps climbing",
          crown: "♾️",
          kind: "par",
          ms: 2400,
        });
      } else {
        showBanner(hud, {
          title: `Welcome, ${identity.handle}`,
          subtitle: `Today's daily · ${course.date}`,
          crown: identity.flag || "🪐",
          kind: "celebrate",
          ms: 2400,
        });
      }
    }
    // Gate on first-hole-of-first-session-ever. The mount function
    // itself checks localStorage and no-ops if the user has seen it
    // before, so we only need to avoid mounting when a resume landed
    // us mid-course — a player on hole 4 doesn't need the "drag to
    // aim" hint.
    if (holeIndex === 0) {
      tutorial = mountFirstHoleTutorial({
        canvas: scene.renderer.domElement,
        modeKey: mode.kind,
      });
    }
  })();

  // Dev-only debug hook so you can poke `window.__cosmic` in the
  // console to inspect live hole state, force-load a hole, etc.
  if (import.meta.env.DEV) {
    (window as unknown as { __cosmic?: unknown }).__cosmic = {
      get hole() { return hole; },
      get holeIndex() { return holeIndex; },
      get mode() { return mode; },
      get course() { return course; },
      loadHole: (i: number) => loadHole(i),
    };
  }

  // Dev-only hole navigation. Arrow keys in endless mode jump to the
  // next/previous hole without playing it out — handy for eyeballing
  // procedurally generated layouts. Disabled in production builds and
  // non-endless modes so it can't affect scoring.
  const onDevKey = (ev: KeyboardEvent) => {
    if (!import.meta.env.DEV) return;
    if (!isEndless) return;
    // Ignore when typing into an input/textarea.
    const t = ev.target as HTMLElement | null;
    if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
    if (ev.key === "ArrowRight") {
      ev.preventDefault();
      void loadHole(holeIndex + 1);
    } else if (ev.key === "ArrowLeft") {
      ev.preventDefault();
      if (holeIndex > 0) void loadHole(holeIndex - 1);
    }
  };
  window.addEventListener("keydown", onDevKey);

  return {
    dispose() {
      unsubFrame();
      aimDispose();
      leaderboard?.dispose();
      if (visuals) {
        scene.scene.remove(visuals.root);
        visuals.dispose();
      }
      if (ghostTrails) {
        scene.scene.remove(ghostTrails.root);
        ghostTrails.dispose();
      }
      if (portals) {
        portals.dispose();
        portals = null;
      }
      scene.scene.remove(sparks.points);
      sparks.dispose();
      joystick.dispose();
      ticker?.dispose();
      window.removeEventListener("keydown", onDevKey);
      if (presenceTimer !== null) {
        window.clearInterval(presenceTimer);
        presenceTimer = null;
      }
      if (presencePill) presencePill.hidden = true;
      if (holePresencePill) holePresencePill.hidden = true;
      settings.dispose();
      endlessChrome.dispose();
      tutorial?.dispose();
      if (isLocalOnly && leaderboardBtn) leaderboardBtn.style.display = "";
    },
  };
}

// Render a white → transparent radial flash at the ball's screen position
// then a full-screen fade as the redirect fires. DOM overlay instead of a
// post-processing pass so it's cheap and doesn't conflict with the bloom
// composer's HDR pipeline.
function triggerWarpOverlay(_x: number, _z: number): void {
  let overlay = document.getElementById("warp-overlay");
  if (!overlay) {
    overlay = document.createElement("div");
    overlay.id = "warp-overlay";
    overlay.style.cssText = [
      "position:fixed",
      "inset:0",
      "pointer-events:none",
      "z-index:60",
      "background:radial-gradient(circle at 50% 55%, rgba(255,255,255,0.95) 0%, rgba(120,255,180,0.8) 25%, rgba(30,20,80,0.95) 70%, rgba(0,0,0,1) 100%)",
      "opacity:0",
      "transition:opacity 0.55s ease-out",
    ].join(";");
    document.body.appendChild(overlay);
  }
  requestAnimationFrame(() => {
    if (overlay) overlay.style.opacity = "1";
  });
}

// Endless escalation curve. H1 starts around 0.27 (gentle), H7 ≈ 0.99
// (matches end of daily), H10 ≈ 1.35, H15 ≈ 1.95 — no cap. The
// generator's clamp01() will handle values above 1 reasonably, and
// downstream visuals (gates, wind, capsule chains) layer extra
// difficulty features as escalation climbs.
export function escalationFor(n: number): number {
  return 0.15 + n * 0.12;
}

// Seed a tiny single-hole "course" for endless mode so the standard
// state machine has something to point at for hole 0 before the
// on-demand generator kicks in.
function buildEndlessPrefix(rngSeed: string): DailyCourse {
  const firstSeed = seedFromString(`endless/${rngSeed}/0`);
  // Endless H1 follows the daily teaching curve: one planet placed off
  // the tee→target line. `loadHole` will call buildBlockerHole for H2
  // and buildHoleAtEscalation from H3 on.
  const built = (courseModule as any).buildIntroHole
    ? (courseModule as any).buildIntroHole(0, firstSeed)
    : buildHole(0, firstSeed, { escalation: 0 });
  return {
    date: `endless/${rngSeed}`,
    seed: firstSeed,
    holes: [built],
  };
}

// Endless mode HUD chrome: a 🏠 is already provided globally by main.ts.
// We still inject an endless-specific escalation pip so players can see
// how brutal the current hole is relative to the daily ceiling.
function mountEndlessChrome(
  mode: GameMode,
  getHoleIndex: () => number,
): { dispose(): void; refresh(): void } {
  if (mode.kind !== "endless") return { dispose() {}, refresh() {} };

  const leftGroup = document
    .getElementById("hud-top")
    ?.querySelector<HTMLElement>(".left");
  if (!leftGroup) return { dispose() {}, refresh() {} };

  const pill = document.createElement("div");
  pill.className = "pill";
  pill.id = "hud-endless-pip";
  pill.style.fontSize = "12px";
  pill.style.letterSpacing = "0.04em";

  const refresh = () => {
    const n = getHoleIndex();
    const e = escalationFor(n);
    // 5-pip scale capped at 1.6 so later holes still show "full".
    const filled = Math.max(0, Math.min(5, Math.round((e / 1.6) * 5)));
    const pips = "●".repeat(filled) + "○".repeat(Math.max(0, 5 - filled));
    pill.innerHTML = `<span class="dim">e=${e.toFixed(2)}</span> <span style="color:var(--accent);">${pips}</span>`;
  };
  refresh();
  leftGroup.appendChild(pill);

  return {
    dispose() {
      pill.remove();
    },
    refresh,
  };
}

